import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  ArrowBack as ArrowBackIcon,
  PriorityHigh as HighPriorityIcon,
  Alarm as MediumPriorityIcon,
  LowPriority as LowPriorityIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import api from '../services/api';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const ResponsibleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [responsible, setResponsible] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const theme = useTheme();

  useEffect(() => {
    const fetchResponsibleAndProjects = async () => {
      try {
        setLoading(true);
        
        // Buscar dados do responsável
        const responsibleResponse = await api.get(`/technical-responsibles/${id}`);
        setResponsible(responsibleResponse.data);
        
        // Buscar todos os projetos
        const projectsResponse = await api.get('/projects/');
        
        // Filtrar projetos deste responsável
        const responsibleProjects = projectsResponse.data.filter(
          project => project.responsible === responsibleResponse.data.full_name
        );
        
        setProjects(responsibleProjects);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setNotification({
          open: true,
          message: 'Erro ao carregar os dados do responsável',
          severity: 'error'
        });
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResponsibleAndProjects();
  }, [id, navigate]);

  const handleBack = () => {
    navigate('/responsibles');
  };

  const handleNavigateToProject = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Calcular resumo de projetos por prioridade
  const getProjectsSummary = () => {
    const summary = {
      alta: projects.filter(p => p.priority === 'Alta').length,
      media: projects.filter(p => p.priority === 'Média').length,
      baixa: projects.filter(p => p.priority === 'Baixa').length,
      total: projects.length
    };
    
    return summary;
  };

  const getInitials = (name) => {
    const names = name.split(' ');
    return names[0].charAt(0) + names[names.length - 1].charAt(0);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!responsible) {
    return (
      <Container maxWidth="lg">
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 3, mb: 2 }}>
          Voltar
        </Button>
        <Alert severity="error">Responsável técnico não encontrado</Alert>
      </Container>
    );
  }

  const summary = getProjectsSummary();

  const profileColor = theme.palette.primary.main;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 6, px: { xs: 2, md: 3 } }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={handleBack}
        variant="outlined"
        sx={{ mb: 3 }}
      >
        Voltar para Lista de Responsáveis
      </Button>

      <Grid container spacing={3}>
        {/* Perfil do Responsável */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} /> Perfil
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box 
              sx={{ 
                p: 4, 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {responsible.profile_image_url ? (
                <Avatar 
                  src={responsible.profile_image_url}
                  alt={responsible.full_name}
                  sx={{ 
                    width: 130, 
                    height: 130, 
                    fontSize: '2.5rem', 
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[3],
                    mb: 2,
                    transition: 'all 0.3s ease',
                    objectFit: 'cover',
                    ':hover': {
                      transform: 'scale(1.05)',
                      boxShadow: theme.shadows[6],
                    }
                  }}
                />
              ) : (
                <Avatar 
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    fontSize: '2rem', 
                    bgcolor: profileColor,
                    boxShadow: theme.shadows[3],
                    mb: 2,
                    transition: 'all 0.3s ease',
                    ':hover': {
                      transform: 'scale(1.05)',
                      boxShadow: theme.shadows[6],
                    }
                  }}
                >
                  {getInitials(responsible.full_name)}
                </Avatar>
              )}
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>
              {responsible.full_name}
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {responsible.position && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{responsible.position}</Typography>
                </Box>
              )}
              
              {responsible.department && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{responsible.department}</Typography>
                </Box>
              )}
              
              {responsible.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{responsible.email}</Typography>
                </Box>
              )}
              
              {responsible.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{responsible.phone}</Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Chip 
                label={responsible.is_active ? 'Ativo' : 'Inativo'} 
                color={responsible.is_active ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>

            {responsible.notes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">Observações:</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {responsible.notes}
                </Typography>
              </Box>
            )}
            
            {/* Resumo de Projetos (integrado no card de perfil) */}
            <Box sx={{ mt: 4, pt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  fontWeight: 'medium',
                  mb: 1.5
                }}
              >
                <AssignmentIcon sx={{ mr: 1, fontSize: '1rem' }} /> Resumo de Projetos
              </Typography>
              
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Card 
                    sx={{ 
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      color: theme.palette.error.dark,
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.15),
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      py: 1, 
                      px: 1.5,
                      '&:last-child': { pb: 1 },
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: 'error.main', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mr: 1,
                          color: 'white'
                        }}>
                          <HighPriorityIcon sx={{ fontSize: '0.8rem' }} />
                        </Box>
                        <Typography variant="caption">Alta</Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="bold">{summary.alta}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card 
                    sx={{ 
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.dark,
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.15),
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      py: 1, 
                      px: 1.5,
                      '&:last-child': { pb: 1 },
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: 'warning.main', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mr: 1,
                          color: 'white'
                        }}>
                          <MediumPriorityIcon sx={{ fontSize: '0.8rem' }} />
                        </Box>
                        <Typography variant="caption">Média</Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="bold">{summary.media}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card 
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.dark,
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      py: 1, 
                      px: 1.5,
                      '&:last-child': { pb: 1 },
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: 'success.main', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mr: 1,
                          color: 'white'
                        }}>
                          <LowPriorityIcon sx={{ fontSize: '0.8rem' }} />
                        </Box>
                        <Typography variant="caption">Baixa</Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="bold">{summary.baixa}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.dark,
                      borderRadius: 2,
                      boxShadow: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      }
                    }}
                  >
                    <CardContent sx={{ 
                      py: 1, 
                      px: 1.5,
                      '&:last-child': { pb: 1 },
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ 
                          width: 24, 
                          height: 24, 
                          borderRadius: '50%', 
                          bgcolor: 'primary.main', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          mr: 1,
                          color: 'white'
                        }}>
                          <WorkIcon sx={{ fontSize: '0.8rem' }} />
                        </Box>
                        <Typography variant="caption">Total</Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="bold">{summary.total}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Lista de Projetos */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Projetos Associados
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {projects.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Este responsável ainda não está vinculado a nenhum projeto.
              </Typography>
            ) : (
              <List>
                {projects.map((project) => (
                  <React.Fragment key={project.project_id}>
                    <ListItem
                      button
                      onClick={() => handleNavigateToProject(project.project_id)}
                      alignItems="flex-start"
                      sx={{ 
                        mb: 1, 
                        borderLeft: '4px solid',
                        borderColor: 
                          project.priority === 'Alta' ? 'error.main' : 
                          project.priority === 'Média' ? 'warning.main' : 'success.main',
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1">{project.project_name}</Typography>
                            <Chip 
                              label={project.priority} 
                              size="small"
                              color={
                                project.priority === 'Alta' ? 'error' : 
                                project.priority === 'Média' ? 'warning' : 'success'
                              }
                            />
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {project.description.length > 150 
                                ? `${project.description.substring(0, 150)}...` 
                                : project.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                              <Typography variant="caption">
                                Início: {new Date(project.start_date).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption">
                                Término: {new Date(project.end_date).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ResponsibleDetails; 